import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Container, Typography, Box, TextField, IconButton, CircularProgress, Snackbar, Avatar,
  Button, Alert
} from '@mui/material';
import { motion } from 'framer-motion';
import { Send, AdminPanelSettings, School } from '@mui/icons-material';
import axios from 'axios';
import io from 'socket.io-client';

const API_BASE_URL = 'https://kidsm.vercel.app';
const SOCKET_URL = 'https://kidssock.onrender.com';
const socket = io(SOCKET_URL, { transports: ['websocket'] });

const SchoolSupportChat = () => {
  const { state } = useLocation();
  const { schoolId } = state || {};
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [conversation, setConversation] = useState(null);
  const messagesEndRef = useRef(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const initializeChat = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get or create conversation with full URL
      const convResponse = await axios.get(`${API_BASE_URL}/api/admin/support/school/conversations`, {
        params: { schoolId },
      });

      let currentConversation;

      if (convResponse.data?.conversations?.length > 0) {
        currentConversation = convResponse.data.conversations[0];
      } else {
        const newConvResponse = await axios.post(`${API_BASE_URL}/api/admin/support/school/messages`, {
          schoolId,
          text: 'Initial support request',
        });

        if (!newConvResponse.data?.conversation) {
          throw new Error('Failed to create new conversation');
        }

        currentConversation = newConvResponse.data.conversation;
      }

      if (!currentConversation?._id) {
        throw new Error('Invalid conversation format - missing ID');
      }

      setConversation(currentConversation);

      // Get messages with full URL
      const msgResponse = await axios.get(`${API_BASE_URL}/api/admin/support/school/messages`, {
        params: { conversationId: currentConversation._id },
      });

      if (msgResponse.data?.messages) {
        setMessages(msgResponse.data.messages);
      } else {
        throw new Error('Invalid messages format');
      }

      socket.emit('joinSupportChat', currentConversation._id);

      socket.on('newSupportMessage', (message) => {
        if (message?.conversationId === currentConversation._id) {
          setMessages((prev) => [...prev, message]);
          scrollToBottom();
        }
      });

      // Handle socket connection errors
      socket.on('connect_error', (err) => {
        console.error('Socket connection error:', err);
        setSnackbar({
          open: true,
          message: 'Connection to chat server failed. Trying to reconnect...',
          severity: 'error'
        });
      });

    } catch (err) {
      console.error('Error initializing chat:', err);
      setError(err.response?.data?.message || err.message || 'Failed to initialize chat');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!schoolId) {
      setError('School ID is missing');
      setLoading(false);
      return;
    }

    initializeChat();

    return () => {
      socket.off('newSupportMessage');
      socket.off('connect_error');
    };
  }, [schoolId]);

  const sendMessage = async () => {
    if (!newMessage.trim() || !conversation?._id) {
      setSnackbar({ open: true, message: 'Cannot send message - missing conversation ID', severity: 'error' });
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/api/admin/support/school/messages`, {
        schoolId,
        text: newMessage,
        conversationId: conversation._id,
      });

      if (response.data?.success) {
        setMessages((prev) => [
          ...prev,
          {
            senderId: schoolId,
            receiverId: 'vendor-admin',
            text: newMessage,
            createdAt: new Date().toISOString(),
            senderType: 'school',
            _id: Date.now().toString(),
            conversationId: conversation._id,
          },
        ]);

        setNewMessage('');
        scrollToBottom();
      } else {
        throw new Error(response.data?.message || 'Failed to send message');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setSnackbar({ open: true, message: err.message || 'Failed to send message', severity: 'error' });
    }
  };

  if (!schoolId) {
    return (
      <Container maxWidth="md" sx={{ py: 4, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
        <Typography variant="h6" sx={{ textAlign: 'center', color: '#e74c3c' }}>
          School ID is required
        </Typography>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <CircularProgress color="primary" />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
          <Typography sx={{ fontSize: '1rem', color: '#e74c3c', mb: 2, textAlign: 'center' }}>{error}</Typography>
          <Button
            variant="contained"
            onClick={initializeChat}
            sx={{ bgcolor: '#6200ee', borderRadius: 2, textTransform: 'none' }}
          >
            Try Again
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <Box sx={{ bgcolor: '#6200ee', p: 2, mb: 2, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ color: '#fff', textAlign: 'center', fontWeight: 'bold' }}>
            Admin Support
          </Typography>
        </Box>

        <Box
          sx={{
            maxHeight: '60vh',
            overflowY: 'auto',
            bgcolor: '#fff',
            borderRadius: 2,
            p: 2,
            mb: 2,
          }}
        >
          {messages.map((message, index) => (
            <Box
              key={message._id || index}
              sx={{
                display: 'flex',
                flexDirection: message.senderType === 'school' ? 'row-reverse' : 'row',
                alignItems: 'flex-end',
                mb: 1,
                maxWidth: '100%',
              }}
            >
              <Avatar
                sx={{
                  bgcolor: '#6200ee',
                  width: 32,
                  height: 32,
                  mr: message.senderType === 'school' ? 0 : 1,
                  ml: message.senderType === 'school' ? 1 : 0,
                }}
              >
                {message.senderType === 'school' ? <School /> : <AdminPanelSettings />}
              </Avatar>
              <Box
                sx={{
                  maxWidth: '70%',
                  bgcolor: message.senderType === 'school' ? '#6200ee' : '#e5e5ea',
                  color: message.senderType === 'school' ? '#fff' : '#000',
                  p: 1.5,
                  borderRadius: 3,
                  borderBottomRightRadius: message.senderType === 'school' ? 0 : 3,
                  borderBottomLeftRadius: message.senderType === 'school' ? 3 : 0,
                }}
              >
                <Typography sx={{ fontSize: '0.875rem' }}>{message.text}</Typography>
                <Typography sx={{ fontSize: '0.625rem', textAlign: 'right', opacity: 0.7 }}>
                  {new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </Typography>
              </Box>
            </Box>
          ))}
          <div ref={messagesEndRef} />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#fff', borderRadius: 2, p: 1 }}>
          <TextField
            fullWidth
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            placeholder="Type a message..."
            multiline
            maxRows={4}
            sx={{ bgcolor: '#fff', borderRadius: 2 }}
            variant="outlined"
            size="small"
          />
          <IconButton
            onClick={sendMessage}
            sx={{ bgcolor: '#6200ee', color: '#fff', ml: 1, '&:hover': { bgcolor: '#3700b3' } }}
          >
            <Send />
          </IconButton>
        </Box>
      </motion.div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SchoolSupportChat;